import { prepareAttachedField } from '@lib/utils/prepareAttachedField';
import { AttachmentIntendedUse } from '@lib/enums/attachments';
import { AttachmentWithIntendedUse } from './findAttachmentByIntendedUse';

// Input object for attachment mutations with an `intendedUse` metadata tag.
export interface AttachmentInput {
  id?: string;
  file?: unknown;
  filename?: string;
  metadata?: {
    intendedUse: AttachmentIntendedUse;
  };
  _destroy?: boolean;
}

// Prepares attachment inputs for a single-attachment field that carries an
// `intendedUse` tag (widget icon / widget image). Returns an array because a
// single logical change can produce up to two mutation entries: a destruction
// marker for the previous attachment plus the new upload.
//
// Cases:
//   1. No form value and no original        → []
//   2. No form value but had original       → [{ id: originalId, _destroy }]
//   3a. New upload with different id        → [destroy old, new upload + metadata]
//   3b. New upload with same id (re-upload) → [new upload + metadata]
//   3c. Unchanged (has urls, no file)       → []
//
// The shape transform for 3a/3b/3c is delegated to `prepareAttachedField`.
export default function prepareAttachmentInputs(
  formValue: Record<string, unknown> | null | undefined,
  originalAttachment: AttachmentWithIntendedUse | undefined,
  intendedUse: AttachmentIntendedUse,
): AttachmentInput[] {
  const result: AttachmentInput[] = [];

  // Case 1 & 2: no form value. If there was an original, destroy it.
  if (!formValue) {
    if (originalAttachment) {
      result.push({ id: originalAttachment.id, _destroy: true });
    }
    return result;
  }

  const prepared = prepareAttachedField(formValue);

  // Case 3c: unchanged API attachment (has urls) or empty value → nothing to send.
  // The `_destroy: true` branch from `prepareAttachedField` only fires for a
  // literal `null`, which we already handled above.
  // eslint-disable-next-line no-underscore-dangle
  if (!prepared || prepared._destroy) return result;

  const formValueId = formValue.id as string | undefined;

  // Case 3a: replacing a different attachment → mark the old one for destruction.
  if (originalAttachment && originalAttachment.id !== formValueId) {
    result.push({ id: originalAttachment.id, _destroy: true });
  }

  const uploadInput: AttachmentInput = {
    ...(prepared as AttachmentInput),
    metadata: { intendedUse },
  };
  if (formValueId !== undefined) {
    uploadInput.id = formValueId;
  }
  result.push(uploadInput);
  return result;
}
